import { Sparkles, Droplets, ShieldCheck } from "lucide-react";

/**
 * Animated CSS/SVG demo of ClearShield in action.
 * Left pane: untreated glass — water spreads, streaks, limescale forms.
 * Right pane: ClearShield treated — water beads up and rolls off.
 */
export default function ClearShieldDemo() {
  return (
    <section
      id="see-it-work"
      data-testid="clearshield-demo"
      className="relative py-24 md:py-32 px-6 md:px-10 bg-gradient-to-b from-slate-50 to-white overflow-hidden"
    >
      <style>{`
        /* Untreated side — water trickles and smears */
        @keyframes untreated-stream {
          0%   { transform: translateY(-20%) scaleY(0.8); opacity: 0; }
          15%  { opacity: 0.75; }
          90%  { opacity: 0.55; }
          100% { transform: translateY(120%) scaleY(2.2); opacity: 0; }
        }
        @keyframes smear-grow {
          0%   { opacity: 0; transform: scaleY(0); }
          40%  { opacity: 0.6; transform: scaleY(1); }
          90%  { opacity: 0.55; }
          100% { opacity: 0.45; transform: scaleY(1); }
        }
        @keyframes limescale-grow {
          0%, 60% { opacity: 0; }
          100%    { opacity: 0.45; }
        }

        /* Treated side — droplets bead and roll cleanly */
        @keyframes drop-fall {
          0%   { transform: translateY(-15%) scale(0.6); opacity: 0; }
          12%  { transform: translateY(10%) scale(1); opacity: 1; }
          70%  { transform: translateY(85%) scale(1); opacity: 1; }
          100% { transform: translateY(115%) scale(0.85); opacity: 0; }
        }

        /* Drift highlight */
        @keyframes shine-sweep {
          0%   { transform: translateX(-120%) rotate(20deg); }
          100% { transform: translateX(220%) rotate(20deg); }
        }

        .glass-pane {
          background:
            linear-gradient(135deg, rgba(255,255,255,0.55) 0%, rgba(220,235,235,0.25) 50%, rgba(180,220,210,0.18) 100%);
          border: 1px solid rgba(255,255,255,0.55);
          box-shadow:
            inset 0 1px 0 rgba(255,255,255,0.7),
            inset 0 -2px 18px rgba(0,40,30,0.10),
            0 20px 50px -20px rgba(0,30,15,0.25);
          backdrop-filter: blur(2px);
        }

        .shine {
          position: absolute;
          inset: -20% -50%;
          background: linear-gradient(115deg, transparent 35%, rgba(255,255,255,0.55) 50%, transparent 65%);
          animation: shine-sweep 7s ease-in-out infinite;
          pointer-events: none;
        }
      `}</style>

      <div className="max-w-7xl mx-auto relative">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 text-xs font-bold tracking-[0.2em] uppercase text-emerald-700">
            <Sparkles size={14} /> See ClearShield in action
          </div>
          <h2 className="mt-4 font-display text-4xl md:text-5xl lg:text-6xl font-light tracking-tight">
            Water hits glass. <br />
            Watch what <span className="italic font-semibold">ClearShield</span> does next.
          </h2>
          <p className="mt-5 text-slate-600 text-lg leading-relaxed max-w-xl">
            A real‑time comparison of untreated glass against ClearShield‑treated glass. Same water. Same gravity. Completely different result.
          </p>
        </div>

        {/* Split demo */}
        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* UNTREATED */}
          <div className="relative">
            <div className="absolute -top-3 left-5 z-10 px-3 py-1 rounded-full bg-amber-100 text-amber-800 text-[10px] font-bold tracking-widest uppercase border border-amber-200">
              Untreated glass
            </div>
            <div className="glass-pane rounded-3xl relative overflow-hidden aspect-[4/5]">
              {/* Limescale haze that fades in */}
              <div
                className="absolute inset-0"
                style={{
                  background:
                    "radial-gradient(ellipse at 30% 40%, rgba(220,210,180,0.55) 0%, transparent 55%), radial-gradient(ellipse at 70% 70%, rgba(200,190,160,0.5) 0%, transparent 60%)",
                  animation: "limescale-grow 6s ease-out infinite alternate",
                }}
              />

              {/* Water streams running down — uneven, smearing */}
              {[12, 28, 45, 62, 78, 88].map((left, i) => (
                <div
                  key={`stream-${i}`}
                  className="absolute top-0 w-[3px] bg-gradient-to-b from-white/70 via-white/30 to-transparent rounded-full"
                  style={{
                    left: `${left}%`,
                    height: "100%",
                    transformOrigin: "top",
                    animation: `untreated-stream ${3.4 + (i % 3) * 0.6}s linear ${i * 0.3}s infinite`,
                    filter: "blur(0.5px)",
                  }}
                />
              ))}

              {/* Stationary smears */}
              {[18, 40, 60, 75].map((left, i) => (
                <div
                  key={`smear-${i}`}
                  className="absolute top-[15%] w-[18px] rounded-full"
                  style={{
                    left: `${left}%`,
                    height: "55%",
                    background:
                      "linear-gradient(180deg, rgba(255,255,255,0.55) 0%, rgba(220,210,180,0.35) 50%, transparent 100%)",
                    transformOrigin: "top",
                    animation: `smear-grow ${5 + i}s ease-out infinite alternate`,
                    filter: "blur(2px)",
                  }}
                />
              ))}

              {/* Frame highlight */}
              <div className="shine" />
            </div>

            <div className="mt-5 px-1">
              <div className="font-display text-xl font-semibold text-emerald-950">
                Streaks. Limescale. Constant scrubbing.
              </div>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">
                Untreated glass is microscopically porous. Water clings, minerals deposit, soap scum bonds — and the only way out is harsh chemicals and elbow grease, every single week.
              </p>
            </div>
          </div>

          {/* TREATED */}
          <div className="relative">
            <div className="absolute -top-3 left-5 z-10 px-3 py-1 rounded-full bg-emerald-600 text-white text-[10px] font-bold tracking-widest uppercase border border-emerald-700/40 shadow-md shadow-emerald-700/20">
              ClearShield treated
            </div>
            <div className="glass-pane rounded-3xl relative overflow-hidden aspect-[4/5]">
              {/* Bouncing/rolling perfect-circle droplets */}
              {[
                { x: 18, size: 18, delay: 0 },
                { x: 32, size: 26, delay: 0.6 },
                { x: 45, size: 14, delay: 1.2 },
                { x: 58, size: 22, delay: 0.3 },
                { x: 70, size: 30, delay: 1.8 },
                { x: 82, size: 12, delay: 0.9 },
                { x: 24, size: 16, delay: 2.4 },
                { x: 50, size: 20, delay: 1.5 },
                { x: 76, size: 18, delay: 2.1 },
                { x: 12, size: 22, delay: 1.7 },
                { x: 88, size: 16, delay: 0.4 },
                { x: 38, size: 14, delay: 2.6 },
              ].map((d, i) => (
                <div
                  key={`drop-${i}`}
                  className="absolute top-0"
                  style={{
                    left: `calc(${d.x}% - ${d.size / 2}px)`,
                    width: `${d.size}px`,
                    height: `${d.size}px`,
                    animation: `drop-fall ${2.8 + (i % 4) * 0.4}s cubic-bezier(0.55, 0, 0.6, 1) ${d.delay}s infinite`,
                    filter: "drop-shadow(0 2px 3px rgba(0,40,30,0.35))",
                  }}
                >
                  <div
                    className="w-full h-full rounded-full relative"
                    style={{
                      background:
                        "radial-gradient(circle at 35% 30%, rgba(255,255,255,0.95) 0%, rgba(220,255,235,0.55) 22%, rgba(60,140,90,0.30) 62%, rgba(0,40,20,0.55) 100%)",
                      border: "1px solid rgba(255,255,255,0.45)",
                    }}
                  >
                    <div
                      className="absolute rounded-full"
                      style={{
                        top: "18%",
                        left: "20%",
                        width: "32%",
                        height: "32%",
                        background:
                          "radial-gradient(circle, rgba(255,255,255,1) 0%, rgba(255,255,255,0) 70%)",
                      }}
                    />
                  </div>
                </div>
              ))}

              {/* Frame highlight */}
              <div className="shine" />
            </div>

            <div className="mt-5 px-1">
              <div className="font-display text-xl font-semibold text-emerald-950">
                Water beads up and rolls clean off.
              </div>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">
                ClearShield bonds to the glass at a molecular level, sealing the pores. Water can't grip. Limescale can't form. A quick rinse and a soft cloth is all it takes — for a decade.
              </p>
            </div>
          </div>
        </div>

        {/* Benefit pills */}
        <div className="mt-14 flex flex-wrap items-center justify-center gap-3">
          {[
            { icon: Droplets, text: "Up to 90% less cleaning time" },
            { icon: ShieldCheck, text: "10‑year guaranteed protection" },
            { icon: Sparkles, text: "Looks new for a decade" },
          ].map((b, i) => {
            const Icon = b.icon;
            return (
              <div
                key={i}
                className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-white border border-emerald-200 text-emerald-900 font-semibold text-sm shadow-sm"
              >
                <Icon size={16} className="text-emerald-600" />
                {b.text}
              </div>
            );
          })}
        </div>

        <div className="mt-10 text-center">
          <a
            href="#booking"
            data-testid="demo-cta-book"
            onClick={(e) => {
              e.preventDefault();
              document.getElementById("booking")?.scrollIntoView({ behavior: "smooth" });
            }}
            className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-emerald-950 text-white font-semibold hover:bg-emerald-700 transition-colors shadow-lg shadow-emerald-950/20"
          >
            Book your application
          </a>
        </div>
      </div>
    </section>
  );
}
